/**
 * ImageUploader - Componente reutilizable para subir imágenes
 * Soporta múltiples imágenes y compresión automática en el backend
 */
import React, { useState, useRef } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { ApiClient } from '../../services/api'
import { getImageUrl } from '../../utils/imageUtils'
import { toastSuccess, toastError } from '../../utils/toastHelper'
import { ConfirmDeleteModal } from '../ui'

interface ImageUploaderProps {
  // Modo: 'single' para una imagen (tipos), 'multiple' para varias (espacios)
  mode: 'single' | 'multiple'

  // Imágenes actuales
  images: string[]

  // Endpoint para subir (sin el dominio, ej: '/api/admin-protected/espacios/123/upload-images')
  uploadEndpoint: string

  // Endpoint para eliminar (opcional, si es diferente)
  deleteEndpoint?: string

  // Callback cuando se actualicen las imágenes
  onImagesUpdated: () => void

  // Texto del botón (opcional)
  buttonText?: string

  // Máximo número de imágenes (solo para mode='multiple')
  maxImages?: number

  // Mostrar contador de imágenes (default: false)
  showCounter?: boolean

  className?: string
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  mode,
  images,
  uploadEndpoint,
  deleteEndpoint,
  onImagesUpdated,
  buttonText,
  maxImages = 10,
  showCounter = false,
  className = ''
}) => {
  const { colors } = useTheme()
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Validar cantidad máxima para múltiples
    if (mode === 'multiple' && images.length + files.length > maxImages) {
      toastError(`Máximo ${maxImages} imágenes permitidas`)
      return
    }

    // Validar que solo sea 1 imagen para modo single
    if (mode === 'single' && files.length > 1) {
      toastError('Solo puedes subir una imagen')
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()

      if (mode === 'single') {
        formData.append('file', files[0])
      } else {
        Array.from(files).forEach(file => {
          formData.append('files', file)
        })
      }

      await ApiClient.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toastSuccess(mode === 'single' ? 'Imagen subida' : `${files.length} imagen(es) subida(s)`)
      onImagesUpdated()

      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

    } catch (error: any) {
      console.error('Error subiendo imagen:', error)
      toastError(error?.response?.data?.detail || 'Error subiendo imagen')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = (index: number) => {
    setImageToDelete(index)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (imageToDelete === null) return

    setDeleting(imageToDelete)

    try {
      const endpoint = deleteEndpoint || `${uploadEndpoint.replace('/upload-images', `/images/${imageToDelete}`)}`

      await ApiClient.delete(endpoint)

      toastSuccess('Imagen eliminada')
      setShowDeleteConfirm(false)
      setImageToDelete(null)
      onImagesUpdated()

    } catch (error: any) {
      console.error('Error eliminando imagen:', error)
      toastError(error?.response?.data?.detail || 'Error eliminando imagen')
    } finally {
      setDeleting(null)
    }
  }

  const canUploadMore = mode === 'single' ? images.length === 0 : images.length < maxImages

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Botón de subir */}
      {canUploadMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={mode === 'multiple'}
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 rounded-lg text-white font-medium transition-all disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}
          >
            {uploading ? (
              <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Subiendo...
              </>
            ) : (
              <>
                📸 {buttonText || (mode === 'single' ? 'Subir Imagen' : 'Subir Imágenes')}
              </>
            )}
          </button>

          {mode === 'multiple' && showCounter && (
            <p className="text-sm text-gray-500 mt-2">
              {images.length} / {maxImages} imágenes
            </p>
          )}
        </div>
      )}

      {/* Grid de imágenes */}
      {images.length > 0 && (
        <div className={`grid ${mode === 'single' ? 'grid-cols-1 max-w-md' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-4`}>
          {images.map((imageUrl, index) => (
            <div key={index} className="relative group">
              {/* Imagen */}
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={getImageUrl(imageUrl)}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzlDQTNBRiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pjwvc3ZnPg=='
                  }}
                />
              </div>

              {/* Botón eliminar */}
              <button
                type="button"
                onClick={() => handleDelete(index)}
                disabled={deleting === index}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                title="Eliminar imagen"
              >
                {deleting === index ? '⏳' : '🗑️'}
              </button>

              {/* Badge de índice */}
              {mode === 'multiple' && (
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  #{index + 1}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Mensaje vacío */}
      {images.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-4xl mb-2">📷</div>
          <p className="text-gray-600">
            {mode === 'single' ? 'No hay imagen' : 'No hay imágenes'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Haz clic en el botón de arriba para subir
          </p>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        title="¿Eliminar imagen?"
        message="¿Estás seguro que deseas eliminar esta imagen?"
        itemName={imageToDelete !== null ? `Imagen #${imageToDelete + 1}` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setImageToDelete(null)
        }}
        isLoading={deleting !== null}
      />
    </div>
  )
}

export default ImageUploader
